import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@televet/kibble-ui/Button';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { Tag } from '@televet/kibble-ui/Tag';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { DeleteDialog } from '@/components/DeleteDialog';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/agent-context-docs/')({
  beforeLoad: () => ({
    pageTitle: 'Context Documents',
  }),
  component: AgentContextDocs,
});

function AgentContextDocs() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);

  const { data } = useQuery(trpc.agentContextDoc.list.queryOptions());

  const { mutate: deleteDoc, isPending: isDeleting } = useMutation(
    trpc.agentContextDoc.deleteOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Document deleted',
          description: data.message,
        });
        queryClient.invalidateQueries({
          queryKey: [['agentContextDoc', 'list']],
        });
        setDeleteDocId(null);
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to delete document',
          description: error.message || 'Failed to delete document',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const filteredDocs = useMemo(
    () =>
      data?.filter((doc) =>
        searchValue
          ? doc.title.toLowerCase().includes(searchValue.toLowerCase()) ||
            doc.description.toLowerCase().includes(searchValue.toLowerCase()) ||
            doc.tags.some((t) =>
              t.toLowerCase().includes(searchValue.toLowerCase())
            )
          : true
      ),
    [data, searchValue]
  );

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        <Flex justify="space-between" align="center">
          <TextInput
            placeholder="Search by title, description, or tag..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            maxW="300px"
          />
          <Button onClick={() => navigate({ to: '/agent-context-docs/add' })}>
            Add Document
          </Button>
        </Flex>
        <Table.Root size="lg" width="100%">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Title</Table.ColumnHeader>
              <Table.ColumnHeader>Provider</Table.ColumnHeader>
              <Table.ColumnHeader>Description</Table.ColumnHeader>
              <Table.ColumnHeader>Tags</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredDocs?.map((doc) => (
              <Table.Row key={doc.id}>
                <Table.Cell fontWeight="medium">{doc.title}</Table.Cell>
                <Table.Cell>
                  <Tag label={doc.provider} variant="default" />
                </Table.Cell>
                <Table.Cell>
                  <Text lineClamp={2} fontSize="sm" variant="subtle">
                    {doc.description}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap={1} flexWrap="wrap">
                    {doc.tags.map((tag) => (
                      <Tag key={tag} label={tag} variant="default" />
                    ))}
                  </Flex>
                </Table.Cell>
                <Table.Cell textAlign="end">
                  <Flex gap={2} justify="flex-end">
                    <Button
                      iconName="externalLink"
                      variant="ghostNeutral"
                      size="sm"
                      onClick={() => window.open(`https://www.notion.so/ottovet/${doc.externalId.replace(/-/g, '')}`, '_blank')}
                    />
                    <Button
                      iconName="pen"
                      variant="ghostNeutral"
                      size="sm"
                      onClick={() =>
                        navigate({
                          to: `/agent-context-docs/${doc.id}`,
                        })
                      }
                    />
                    <Button
                      iconName="trashcan"
                      variant="ghostDestructive"
                      size="sm"
                      onClick={() => setDeleteDocId(doc.id)}
                    />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {filteredDocs?.length === 0 && (
          <Flex justify="center" align="center" p={8}>
            No documents found
          </Flex>
        )}
      </Flex>
      <DeleteDialog
        isOpen={!!deleteDocId}
        title="Delete Document"
        description="Are you sure you want to delete this context document? This action cannot be undone."
        onConfirm={() => deleteDocId && deleteDoc({ id: deleteDocId })}
        onCancel={() => setDeleteDocId(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
